'use client';

import { Link } from '@packages/modules/src/Link/Link';
import { useTrackMarketing } from '@packages/shared/src/hooks/useTrackMarketing/useTrackMarketing';
import { useRef } from 'react';
import { useAssignLinkTargetBasedOnBotStatus } from '../../hooks/useAssignLinkTargetBasedOnBotStatus';
import { buildLinkWithArguments } from '../../utils/buildLinkWithArguments';
import { classes } from '../../utils/classes';
import { Icon } from '../Icon/Icon';
import { SoftHyphenInjector } from '../SoftHyphenInjector';
import type { LinkByTextType } from './types';

const alignmentStyle = {
  left: 'text-left',
  center: 'text-center',
  right: 'text-right',
} as const;

/**
 * LinkByText component displays link with Arguments and an optional Icon with a direction
 * */
export const LinkByText = ({
  data,
  noUnderline,
  className,
}: {
  data: LinkByTextType;
  noUnderline?: boolean;
  className?: string;
}) => {
  const {
    field_link: link,
    field_link_target: linkTarget,
    behavior_paragraph_link_rel: linkRelRaw,
    field_direction: direction,
    field_text: text,
    field_icon: icon,
    behavior_paragraph_alignment: alignment = 'left',
  } = data;
  /**
   * The standard <Link> is not underlined (yet?),
   * but LinkByText is underlined, unless this is explicitly turned off.
   */
  const isUnderlined = !noUnderline;

  const linkRel = linkRelRaw === '_none' ? undefined : linkRelRaw;
  const tmpLink = buildLinkWithArguments(link, data);
  const assignLinkTarget = useAssignLinkTargetBasedOnBotStatus();
  const finalLink = assignLinkTarget(tmpLink);
  const linkRef = useRef<HTMLAnchorElement>(null);
  const { trackClick } = useTrackMarketing(data, linkRef, finalLink);
  return (
    <div className={alignmentStyle[alignment]}>
      <Link
        className={classes(
          'text-color-text-dark decoration-color-text-dark inline-flex! items-center',
          isUnderlined && 'underline',
          className,
        )}
        ref={linkRef}
        href={finalLink}
        onClick={() => trackClick()}
        openInNewTab={!!(linkTarget && linkTarget === '_blank')}
        rel={linkRel}
      >
        <SoftHyphenInjector text={text} />
        {icon && (
          <Icon
            type={icon}
            className={classes('min-w-2 align-middle', direction === 'left' && '-order-1')}
          />
        )}
      </Link>
    </div>
  );
};
